import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Pagination
} from '@mui/material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Page from 'src/components/Page';
import palette from 'src/theme/palette';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddEditFaqCategoryModal from 'src/sections/dashboard-sections/AddEditFaqCategoryModal';
import { getFaqsCategoryList, deleteFaqsCategory } from 'src/ApiServices';

export default function FaqsCategory() {
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  // Pagination states
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
    has_more_pages: false
  });

  const fetchCategories = async (currentPage = 1) => {
    try {
      setLoading(true);
      const response = await getFaqsCategoryList({ page: currentPage });
      setCategories(response?.data && Array.isArray(response.data) ? response.data : []);
      setPagination(
        response.pagination || {
          current_page: 1,
          per_page: 20,
          total: response.data?.length || 0,
          last_page: 1,
          has_more_pages: false
        }
      );
    } catch (error) {
      console.error('Error fetching FAQ categories:', error);
      setCategories([]);
      toast.error('Failed to load FAQ categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories(page);
  }, [page]);

  const handleAddClick = () => {
    setEditingCategory(null);
    setModalOpen(true);
  };

  const handleEditClick = (category) => {
    setEditingCategory(category);
    setModalOpen(true);
  };

  const handleDeleteClick = (category) => {
    setSelectedCategory(category);
    setDeleteModalOpen(true);
  };

  const handleModalSubmit = async (formData) => {
    try {
      await fetchCategories(page);
      toast.success(
        editingCategory ? 'FAQ category updated successfully!' : 'FAQ category added successfully!'
      );
      setModalOpen(false);
      setEditingCategory(null);
    } catch (error) {
      console.error('Error in modal submit:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCategory) return;

    try {
      setDeleteLoading(true);
      await deleteFaqsCategory(selectedCategory.id);
      toast.success('FAQ category deleted successfully!');
      fetchCategories(page);
      setDeleteModalOpen(false);
      setSelectedCategory(null);
    } catch (error) {
      console.error('Failed to delete FAQ category:', error);
      toast.error(error.response?.data?.message || 'Failed to delete FAQ category!');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedCategory(null);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingCategory(null);
  };

  const tableHeaders = ['Name', 'Created Date', 'Updated Date', 'Actions'];

  const renderSkeletonRows = () =>
    Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={index}>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" width={200} height={25} />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" width={120} height={25} />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" width={120} height={25} />
        </TableCell>
        <TableCell sx={{ px: 2, textAlign: 'center' }}>
          <Skeleton
            variant="circular"
            width={40}
            height={40}
            sx={{ display: 'inline-block', mx: 0.5 }}
          />
          <Skeleton
            variant="circular"
            width={40}
            height={40}
            sx={{ display: 'inline-block', mx: 0.5 }}
          />
        </TableCell>
      </TableRow>
    ));

  const renderNoRecordsFound = () => {
    return (
      <TableRow>
        <TableCell
          colSpan={tableHeaders.length}
          sx={{
            textAlign: 'center',
            py: 8,
            borderBottom: 'none'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography
              variant="h6"
              sx={{
                color: palette.text.secondary,
                fontFamily: 'poppinsMedium',
                mb: 1
              }}
            >
              No FAQ Categories Found
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: palette.grey[600],
                fontFamily: 'poppinsRegular',
                maxWidth: 400,
                textAlign: 'center'
              }}
            >
              No FAQ categories available. Add a new category to get started.
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Page title={`FAQ Categories | ${process.env.REACT_APP_PROJECTNAME}`}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            mb: 3,
            mt: 2,
            pb: 2,
            pl: 1,
            gap: 1
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, md: 25 },
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.titledark
            }}
          >
            FAQ Categories
          </Typography>
          <Button
            variant="contained"
            sx={{
              background: palette.primary.main,
              color: palette.primary.contrastText,
              height: 40,
              '&:hover': { background: palette.primary.dark },
              '&:disabled': { background: palette.grey[400] }
            }}
            onClick={handleAddClick}
            startIcon={<AddIcon />}
          >
            Add FAQ Category
          </Button>
        </Box>

        <Card>
          <TableContainer>
            <Table
              sx={{
                '& .MuiTableCell-head': {
                  backgroundColor: palette.background.paper,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsSemiBold',
                  fontSize: '14px',
                  borderBottom: `2px solid ${palette.divider}`,
                  py: 2
                },
                '& .MuiTableCell-body': {
                  color: palette.text.primary,
                  fontFamily: 'poppinsRegular',
                  fontSize: '14px',
                  borderBottom: `1px solid ${palette.divider}`,
                  py: 2
                }
              }}
            >
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header, index) => (
                    <TableCell
                      key={index}
                      sx={{
                        whiteSpace: 'nowrap',
                        px: 2,
                        ...(header === 'Actions' && { width: 120, textAlign: 'center' })
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? renderSkeletonRows()
                  : categories.length > 0
                  ? categories.map((category) => (
                      <TableRow
                        key={category.id}
                        sx={{
                          '&:hover': { backgroundColor: palette.action.hover },
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        <TableCell sx={{ px: 2 }}>{category.name}</TableCell>
                        <TableCell sx={{ px: 2 }}>{formatDate(category.created_at)}</TableCell>
                        <TableCell sx={{ px: 2 }}>{formatDate(category.updated_at)}</TableCell>
                        <TableCell sx={{ px: 2, textAlign: 'center' }}>
                          <Tooltip title="Edit Category">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(category)}
                              sx={{ color: palette.primary.main, mx: 1 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Category">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(category)}
                              sx={{ color: palette.error.main }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  : renderNoRecordsFound()}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Only show pagination if there are records */}
        {!loading && categories.length > 0 && pagination.total > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
            <Pagination
              count={pagination.last_page}
              page={pagination.current_page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}

        {/* Add/Edit Modal */}
        <AddEditFaqCategoryModal
          open={modalOpen}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          editingCategory={editingCategory}
        />

        {/* Delete Confirmation Modal */}
        <Dialog
          open={deleteModalOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
          PaperProps={{
            sx: {
              borderRadius: 2,
              padding: 1
            }
          }}
        >
          <DialogTitle id="delete-dialog-title" sx={{ textAlign: 'center', py: 3 }}>
            <WarningAmberIcon
              sx={{
                fontSize: 60,
                color: palette.warning.main,
                mb: 2
              }}
            />
            <Typography
              variant="h6"
              sx={{ fontFamily: 'poppinsSemiBold', color: palette.text.primary }}
            >
              Confirm Deletion
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
            <DialogContentText
              id="delete-dialog-description"
              sx={{ color: palette.text.secondary, fontFamily: 'poppinsRegular' }}
            >
              Are you sure you want to delete <strong>"{selectedCategory?.name}"</strong>?
              <br />
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3 }}>
            <Button
              onClick={handleDeleteCancel}
              variant="outlined"
              sx={{
                borderColor: palette.grey[400],
                color: palette.text.primary,
                '&:hover': {
                  borderColor: palette.grey[600],
                  backgroundColor: palette.action.hover
                },
                minWidth: 100
              }}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              sx={{
                backgroundColor: palette.error.main,
                color: palette.error.contrastText,
                '&:hover': {
                  backgroundColor: palette.error.dark
                },
                minWidth: 100
              }}
              disabled={deleteLoading}
              startIcon={
                deleteLoading ? (
                  <Skeleton variant="circular" width={20} height={20} />
                ) : (
                  <DeleteIcon />
                )
              }
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Page>
  );
}
